import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaymentIcon from '@mui/icons-material/Payment';
import HomeIcon from '@mui/icons-material/Home';
import { PaynowService, PaymentStatusResponse } from '../api/paynowService';
import PaymentStatusChip from '../components/payments/PaymentStatusChip';

const PaymentReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<PaymentStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [polling, setPolling] = useState(false);

  const paymentId = searchParams.get('paymentId');
  const reference = searchParams.get('reference');
  const paynowReference = searchParams.get('paynowReference');

  const fetchPaymentStatus = async (shouldPoll = false) => {
    if (!paymentId) {
      setError('Payment ID not found in URL parameters');
      setLoading(false);
      return;
    }

    try {
      if (shouldPoll) {
        setPolling(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = shouldPoll 
        ? await PaynowService.pollPaymentStatus(paymentId)
        : await PaynowService.getPaymentStatus(paymentId);
      
      setPayment(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch payment status');
    } finally {
      setLoading(false);
      setPolling(false);
    }
  };

  useEffect(() => {
    fetchPaymentStatus();
  }, [paymentId]);

  const getStatusIcon = () => {
    if (!payment) return null;

    switch (payment.status) {
      case 'PAID':
        return <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />;
      case 'FAILED':
      case 'CANCELLED':
        return <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />;
      default:
        return <HourglassEmptyIcon sx={{ fontSize: 64, color: 'warning.main' }} />;
    }
  };

  const getStatusMessage = () => {
    if (!payment) return '';

    switch (payment.status) {
      case 'PAID':
        return 'Your payment has been successfully processed!';
      case 'FAILED':
        return 'Your payment could not be processed. Please try again.';
      case 'CANCELLED':
        return 'Your payment was cancelled.';
      case 'PENDING':
      case 'INITIATED':
        return 'Your payment is being processed. Please wait...';
      default:
        return 'Payment status is being verified.';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading payment status...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <PaymentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Payment Status
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {payment && (
        <Card elevation={4}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Box sx={{ mb: 3 }}>
              {getStatusIcon()}
            </Box>

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {getStatusMessage()}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <PaymentStatusChip status={payment.status} size="medium" />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2} sx={{ textAlign: 'left' }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Payment ID
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {payment.paymentId}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {formatCurrency(payment.amount)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Paynow Reference
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {payment.paynowReference}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(payment.createdAt)}
                </Typography>
              </Grid>

              {payment.reference && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Reference
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {payment.reference}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {(payment.status === 'PENDING' || payment.status === 'INITIATED') && (
                <Button
                  variant="outlined"
                  onClick={() => fetchPaymentStatus(true)}
                  disabled={polling}
                  startIcon={polling ? <CircularProgress size={16} /> : null}
                >
                  {polling ? 'Checking...' : 'Check Status'}
                </Button>
              )}
              
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>

              <Button
                variant="outlined"
                onClick={() => navigate('/payments')}
              >
                View Payment History
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Auto-refresh for pending payments */}
      {payment && (payment.status === 'PENDING' || payment.status === 'INITIATED') && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            This page will automatically refresh the payment status. 
            You can also manually check the status using the button above.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default PaymentReturn;
